import { Toggle } from 'framework7-react';
import { FC, useMemo, useState } from 'react';

import { EventFilter, EventMetaFilter, PublicFeedEvent } from '@shared/types/event';
import dayjs from 'dayjs';
import { uniq } from 'lodash';

import {
  useCurrentUserEventCritiqueQuery,
  useEventQuery,
  useUnauthorizedEventsQuery,
} from '@queries/EventQueries';

import { useCritiqueDocumentMutation } from '@mutations/CritiqueMutations';

export const SuperAdminEventCategoryTogglesUnwrapped: FC<{
  // Filter values are only present on feed events, not on the event detail fallback
  event: Pick<PublicFeedEvent, '_id'> & Partial<Record<EventFilter, boolean>>;
  eventMetaFilters?: EventMetaFilter[];
}> = ({ event, eventMetaFilters }) => {
  const [eventMetaFiltersValues, setEventMetaFiltersValues] = useState<Record<string, any>>(event);

  const { userEventCritique, refetchUserEventCritique } = useCurrentUserEventCritiqueQuery(
    event._id,
  );
  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();

  const handleChangeEventMetaFilter = async (filter: EventMetaFilter, toggleEvent: any) => {
    const updatedEventMetaFiltersValues = {
      ...eventMetaFiltersValues,
      [filter.value]: toggleEvent.target.checked,
    };

    setEventMetaFiltersValues(updatedEventMetaFiltersValues);

    const like = [...(userEventCritique?.like ?? []), ...(filter.cta?.suggestion?.like ?? [])];
    const updatedLike = uniq(
      toggleEvent.target.checked
        ? like
        : like.filter((l) => !filter.cta?.suggestion?.like.includes(l)),
    );

    await critiqueDocumentAsync({
      documentId: event._id,
      documentType: 'events',
      like: updatedLike,
    });
  };

  if (!eventMetaFilters?.length) {
    return <></>;
  }

  return (
    <div className="pb-2 d-grid gap-2">
      {eventMetaFilters?.map((filter) => (
        <div key={filter.value} className="d-flex align-items-center justify-content-between">
          <strong>{filter.label}</strong>
          <div>
            <Toggle
              defaultChecked={eventMetaFiltersValues[filter.value]}
              onChange={(event) => handleChangeEventMetaFilter(filter, event)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const SuperAdminEventCategoryToggles: FC<{
  eventId: string;
}> = ({ eventId }) => {
  const { event: fetchedEvent } = useEventQuery(eventId);
  const { events = [], eventsMeta } = useUnauthorizedEventsQuery(
    {
      cityId: fetchedEvent?.cityId,
      startDate: dayjs(fetchedEvent?.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(fetchedEvent?.startDate).format('YYYY-MM-DD'),
      rsvpData: 'ignore',
      forceSupply: 'true',
    },
    Boolean(fetchedEvent?.startDate && fetchedEvent?.cityId),
  );

  const event = useMemo(() => {
    return events.find((event) => event._id === eventId);
  }, [events, eventId]);

  if (!event && !fetchedEvent) {
    return <></>;
  }

  return (
    <SuperAdminEventCategoryTogglesUnwrapped
      event={event ?? fetchedEvent}
      eventMetaFilters={eventsMeta?.filters ?? []}
    />
  );
};
